/* eslint-disable import/no-dynamic-require */
/* eslint-disable global-require */
import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { GridOnOutlined, FormatListBulletedOutlined } from '@material-ui/icons';
import { Popover, PopoverBody } from 'reactstrap';
import { renderInfoForType, EMPTY_ARRAY } from '../../utils/helpers';
import AttachmentDetail from '../../modules/forms/components/AttachmentDetail';
import FormsMap from '../FormsMap';
import styles from './customField.module.scss';
import CheckListDisplay from '../../modules/forms/completeForm/CheckListDisplay';
import Annotations from '../Annotations/Annotation';
import Clickable from '../clickable/Clickable';
import FormattedMessageWithClass from '../formattedMessageWithClass';
import PictureDraggable from '../../modules/sms/components/PictureDraggable';
// eslint-disable-next-line import/no-cycle
import FormDataSourceDetails from './FormDataSourceDetails';
import MultiLocation from '../../pulpo_visualizer/fields/multi_location/multiLocation';
import MapLocationDetail from './MapLocationDetail';

import {
  normalizePreviewValue,
  getDs6PreviewColumns,
  getDs6ColumnTitle,
  getDs6ColumnValue
} from '../../utils/ds6Helpers';


const getDs6SelectionIdentifier = (value) => {
  if (!value || typeof value !== 'object') return '';
  return value?.detailKey || value?.detailId || value?.key || value?.id || '';
};

const isValidURL = (url) => {
  if (typeof url !== 'string' || url.length < 4) return false;
  const urlToTest = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
  try {
    const parsed = new URL(urlToTest);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch (e) {
    return false;
  }
};

const CustomFieldDetail = (props) => {
  const {
    data,
    fields,
    version,
    location,
    mapStyle,
    noBorder,
    orderingField,
    adp,
    allDataSourcesFromStore,
    form_entry_data,
    formApprovalProcess,
    profile,
    fromBadgeSignatory,
    FormDetails,
    formDetails,
  printView, customPrintView, enabled_zoomed,
    badging,
    badgeData,
    mcgHeader,
    showMultiColTitle,
    user,
    selectedLang,
    linkedFormAnswerDetails,
    intl
  } = props;

  const [popoverData, setPopoverData] = useState({ isOpenPopover: false, selectedId: '' });
  const [terminalModal, setTerminalModal] = useState({ terminalModal: false, fieldId: '' });
  const [multiFieldViewModes, setMultiFieldViewModes] = useState({});

  const getMultiFieldViewMode = fieldId => multiFieldViewModes[fieldId] || 'list';
  const setMultiFieldView = (fieldId, mode) => {
    setMultiFieldViewModes(prev => ({ ...prev, [fieldId]: mode }));
  };
  const allDataSources = Array.isArray(allDataSourcesFromStore) ? allDataSourcesFromStore : [];

  const linkedFormEntriesMap = useMemo(() => {
    const map = new Map();
    if (Array.isArray(linkedFormAnswerDetails)) {
      linkedFormAnswerDetails.forEach((answer) => {
        const id = getDs6SelectionIdentifier(answer);
        if (id) {
          map.set(String(id), answer);
        }
      });
    }
    return map;
  }, [linkedFormAnswerDetails]);

  const getMultiFieldRows = useCallback((field) => {
    const value = data?.[field?.id];
    if (Array.isArray(value)) return value;
    if (value && typeof value === 'object') return [value];
    return [];
  }, [data]);


  const mcgDisplayColumnsMap = useMemo(() => {
    const map = {};
    (fields || []).forEach((f) => {
      if (f.type === 'multi_field') {
        const columns = Object.keys(f?.multiple_fields || {}).reduce((cols, multiFieldId) => {
          cols.push({ kind: 'field', id: multiFieldId });

          getDs6PreviewColumns(f, multiFieldId).forEach((column) => {
            cols.push({
              kind: 'ds6_preview',
              id: `${multiFieldId}__${column.id}`,
              multiFieldId,
              columnId: column.id,
              columnConfig: column.config
            });
          });

          return cols;
        }, []);
        map[f.id] = columns;
      }
    });
    return map;
  }, [fields]);


  const getMultiFieldSelectedDs6Entry = (multiData, multiFieldId) => {
    const selectedAnswer = multiData?.[multiFieldId];
    if (!selectedAnswer || Array.isArray(selectedAnswer)) return null;

    if (typeof selectedAnswer !== 'object') {
      return null;
    }

    const cachedEntry = selectedAnswer?.entry_data || selectedAnswer?.entryData;
    if (cachedEntry && typeof cachedEntry === 'object') return cachedEntry;

    const selectedDetailId = getDs6SelectionIdentifier(selectedAnswer);
    if (!selectedDetailId) return null;

    return linkedFormEntriesMap.get(String(selectedDetailId)) || null;
  };

  const getMultiFieldDs6ColumnTitle = (field, multiFieldId, columnId, columnConfig) => (
    getDs6ColumnTitle(multiFieldId, columnId, columnConfig, field, allDataSources));

  const getMultiFieldDs6ColumnValue = (field, multiFieldId, entry, columnId) => getDs6ColumnValue({
    entry,
    columnId,
    multiFieldId,
    currentField: field,
    allDataSources,
    renderInfoParams: { selectedLang, user }
  });

  const getMultiFieldRowKey = (fieldId, multiData, index) => `${fieldId}-${multiData?.id || multiData?.key || index}`;

  const getMultiFieldHeaderContent = (field, multiData, column) => {
    const multiFieldId = column?.multiFieldId || column?.id;
    const isDs6Preview = column?.kind === 'ds6_preview';
    const translatedName = field?.multiple_fields?.[multiFieldId]?.meta_data?.[selectedLang]?.name;

    if (isDs6Preview) {
      return getMultiFieldDs6ColumnTitle(field, column.multiFieldId, column.columnId, column.columnConfig);
    }

    if (selectedLang && selectedLang !== '' && translatedName) {
      return translatedName;
    }

    if (field?.multiple_fields?.[multiFieldId]?.type?.key === 'multi_level' && multiData?.[multiFieldId]) {
      return (
        <span className={styles.multiWrap}>
          {Object.values(multiData?.[multiFieldId]).filter(Boolean).map((category, i) => (
             <div key={`${multiFieldId}-${category}`} className={styles.multiNested}>
              <span>
                {i === 0 ? field?.multiple_fields?.[multiFieldId]?.title : `${field?.multiple_fields?.[multiFieldId]?.title || 'Level'} ${i + 1}`}
              </span>
             </div>
          ))}
        </span>
      );
    }

    return field?.multiple_fields?.[multiFieldId]?.title;
  };

  const getMultiFieldDs6DisplayValue = (field, multiFieldId, multiData) => {
    const selectedAnswer = multiData?.[multiFieldId];
    if (Array.isArray(selectedAnswer)) {
      const labels = selectedAnswer
        .map(answer => normalizePreviewValue(answer?.value || answer?.label || answer))
        .filter(Boolean)
        .join(', ');

      if (labels) return labels;
    } else if (selectedAnswer && typeof selectedAnswer === 'object') {
      const label = normalizePreviewValue(selectedAnswer?.value || selectedAnswer?.label || selectedAnswer?.name);

      if (label) return label;
    }
    return normalizePreviewValue(selectedAnswer);
  };

  const renderMultiFieldCellValue = (field, multiFieldId, multiData, rowIndex) => {
    const multiField = field?.multiple_fields?.[multiFieldId];
    const fieldValue = multiData?.[multiFieldId];
    const fieldType = multiField?.type?.key;

    if (fieldType === 'select') {
      if (Array.isArray(fieldValue) && fieldValue.length > 0 && typeof fieldValue[0] === 'object' && fieldValue[0]?.label) {
        return fieldValue.map(item => item.label).join(', ');
      }
      return fieldValue || '';
    }

    if (fieldType === 'dataSource-ds6') {
      return getMultiFieldDs6DisplayValue(field, multiFieldId, multiData);
    }

    if (fieldType === 'string' && typeof fieldValue === 'string' && fieldValue.length > 40) {
      return handleMultiFieldString(
        fieldValue,
        multiFieldId,
        rowIndex,
        multiField?.validator?.multiple_email
      );
    }

    const isString = typeof fieldValue === 'string';
    const isLink = multiField?.validator?.type === 'link';
    const isURL = isString && (fieldValue.startsWith('http://') || fieldValue.startsWith('https://'));

    if (fieldValue && isString && (isLink || isURL)) {
      if (!isValidURL(fieldValue)) {
        return fieldValue;
      }
      const formattedUrl = fieldValue.startsWith('http://') || fieldValue.startsWith('https://')
        ? fieldValue
        : `https://${fieldValue}`;

      const handleClick = (e) => {
        if (!isValidURL(fieldValue)) {
          e.preventDefault();
        }
      };

      return (
        <a
          href={formattedUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
          style={{
            color: '#0066cc',
            textDecoration: 'underline',
            wordBreak: 'break-all'
          }}
        >
          {fieldValue}
        </a>
      );
    }

    const displayType = fieldType;

    return renderInfoForType(
      {
        ...multiField,
        id: multiFieldId,
        type: displayType,
        validators: multiField?.inputType
      },
      fieldValue,
      version,
      true,
      null,
      profile,
      false, // fromBadgeSignatory
      null // sendWithSecond
    );
  };

  const renderMultiFieldContent = (f) => {
    const displayColumns = mcgDisplayColumnsMap[f?.id] || [];
    const rows = getMultiFieldRows(f);
    if (!rows.length) {
      return (
        <div style={{ padding: '20px', textAlign: 'center', color: '#8C9EA7' }}>
          <FormattedMessage id="form.no_data" defaultMessage="No data available" />
        </div>
      );
    }

    const renderCellContent = (column, multi_data, idx) => {
      const multi_field = column?.multiFieldId || column?.id;
      const isDs6Preview = column?.kind === 'ds6_preview';
      if (isDs6Preview) {
        return (
          <p style={{ wordBreak: f?.show_refresh_button ? 'unset' : 'break-word', margin: 0 }}>
            {getMultiFieldDs6ColumnValue(
              f, column.multiFieldId, getMultiFieldSelectedDs6Entry(multi_data, column.multiFieldId), column.columnId
            )}
          </p>
        );
      }
      const typeKey = f?.multiple_fields?.[multi_field]?.type?.key;
      if (typeKey === 'multi_level') {
        return (
          <div className={styles.multipleFields}>
            {multi_data?.[multi_field]
              && Object.values(multi_data?.[multi_field] || {}).filter(Boolean)
                .map(cat => (cat && cat !== '' ? (<div key={cat} className={styles.multiDataWrap}><p>{cat}</p></div>) : null))}
          </div>
        );
      }
      if (typeKey === 'dataSource-ds8') {
        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {f?.multiple_fields?.[multi_field]?.levels && Object.keys(f?.multiple_fields?.[multi_field]?.levels).map(level => (
              <div key={`${f.id}-${multi_field}-${level}`} className={styles.multiGridDataSource}>
                <span>{f?.multiple_fields?.[multi_field]?.levels[level]?.title || level?.replace('_', ' ')}</span>
                <p>{multi_data?.[multi_field]?.[level]}</p>
              </div>
            ))}
          </div>
        );
      }
      if (typeKey === 'attachments') {
        return (
          <p style={{ wordBreak: f?.show_refresh_button ? 'unset' : 'break-word', margin: 0 }}>
            <AttachmentDetail
              attachments={multi_data?.[multi_field]}
              title={f?.multiple_fields?.[multi_field]?.title}
              field={f}
              displayLink
              enabled_zoomed={enabled_zoomed}
            />
          </p>
        );
      }

      return (
        <div style={{ wordBreak: f?.show_refresh_button ? 'unset' : 'break-word', margin: 0 }}>
          <div>{renderMultiFieldCellValue(f, multi_field, multi_data, idx)}</div>
        </div>
      );
    };

    const isPrintMode = version === 'isPrint' || printView || customPrintView;
    const viewMode = isPrintMode ? 'list' : getMultiFieldViewMode(f.id);

    return (
      <div style={{
        width: '100%',
        maxWidth: '100%',
        padding: '0 0 15px 0',
        boxSizing: 'border-box'
      }}>
        {showMultiColTitle && mcgHeader && (
          <div key={f?.id} className={`${styles.infoFull} ${styles.headerSty}`}>
            <h3>{f?.title}</h3>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '15px' }}>
          {!isPrintMode && (
            <div style={{ display: 'flex', gap: '4px', color: '#8C9EA7', flexShrink: 0 }}>
              <span
                role="button"
                tabIndex={0}
                onClick={() => setMultiFieldView(f.id, 'grid')}
                onKeyDown={(e) => { if (e.key === 'Enter') setMultiFieldView(f.id, 'grid'); }}
                style={{ cursor: 'pointer', lineHeight: 0 }}
                title={intl?.formatMessage
                  ? intl.formatMessage({ id: 'form.view.grid', defaultMessage: 'Grid view' })
                  : 'Grid view'}
                aria-label={intl?.formatMessage
                  ? intl.formatMessage({ id: 'form.view.grid_aria', defaultMessage: 'Switch to grid view' })
                  : 'Switch to grid view'}
              >
                <GridOnOutlined style={{ fill: getMultiFieldViewMode(f.id) === 'grid' ? 'var(--primary-color)' : undefined }} />
              </span>
              <span
                role="button"
                tabIndex={0}
                onClick={() => setMultiFieldView(f.id, 'list')}
                onKeyDown={(e) => { if (e.key === 'Enter') setMultiFieldView(f.id, 'list'); }}
                style={{ cursor: 'pointer', lineHeight: 0 }}
                title={intl?.formatMessage
                  ? intl.formatMessage({ id: 'form.view.list', defaultMessage: 'List view' })
                  : 'List view'}
                aria-label={intl?.formatMessage
                  ? intl.formatMessage({ id: 'form.view.list_aria', defaultMessage: 'Switch to list view' })
                  : 'Switch to list view'}
              >
                <FormatListBulletedOutlined style={{ fill: getMultiFieldViewMode(f.id) === 'list' ? 'var(--primary-color)' : undefined }} />
              </span>
            </div>
          )}
        </div>

        {isPrintMode && (() => {
          const showLimitedColumnsPerRow = 6;
          const chunkHeaders = (head, size) => {
            const chunks = [];
            for (let i = 0; i < head.length; i += size) {
              chunks.push(head.slice(i, i + size));
            }
            return chunks;
          };
          const headerChunks = chunkHeaders(displayColumns, showLimitedColumnsPerRow);

          return headerChunks.map((headersChunk, chunkIndex) => (
            <div key={`chunk-${headersChunk[0]?.id || chunkIndex}`} style={{ marginBottom: '10px' }} className={styles.multiFieldPrintWrapper}>
              <table className={styles.multiFieldPrintTable}>
                <thead>
                  <tr>
                    {headersChunk.map(column => (
                      <th key={`print-hdr-${column.id}`}>
                        {getMultiFieldHeaderContent(f, rows[0], column)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((multi_data, rowIndex) => (
                    <tr key={getMultiFieldRowKey(f.id, multi_data, rowIndex)}>
                      {headersChunk.map(column => (
                        <td key={`print-${getMultiFieldRowKey(f.id, multi_data, rowIndex)}-${column.id}`}>
                          {renderCellContent(column, multi_data, rowIndex)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ));
        })()}

        {!isPrintMode && viewMode === 'grid' && (
          <div style={{ overflowX: 'auto', width: '100%', paddingBottom: '10px' }}>
            <div style={{ minWidth: '900px' }}>
              {rows.map((multi_data, index) => {
                const rowKey = getMultiFieldRowKey(f.id, multi_data, index);

                return (
                  <div key={rowKey} className={styles.mcgGridContainer}>
                    {displayColumns.map(column => (
                      <div key={`${rowKey}-${column.id}`} className={styles.mcgGridItem}>
                        <span>{getMultiFieldHeaderContent(f, multi_data, column)}</span>
                        {renderCellContent(column, multi_data, index)}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!isPrintMode && viewMode !== 'grid' && (
          <div style={{ overflowX: 'auto', width: '100%', paddingBottom: '10px' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: displayColumns.map((column) => {
                const multiFieldId = column?.multiFieldId || column?.id;
                const typeKey = f?.multiple_fields?.[multiFieldId]?.type?.key;
                return typeKey === 'multi_level' ? 'minmax(600px, 2fr)' : 'minmax(300px, 1fr)';
              }).join(' '),
              minWidth: `${displayColumns.reduce((acc, column) => {
                const multiFieldId = column?.multiFieldId || column?.id;
                const typeKey = f?.multiple_fields?.[multiFieldId]?.type?.key;
                return acc + (typeKey === 'multi_level' ? 600 : 300);
              }, 0)}px`,
              borderTop: '1px solid #e6eaee',
              borderLeft: '1px solid #e6eaee'
            }}>
              {displayColumns.map(column => (
                  <div
                   key={`lhdr-${column.id}`}
                   style={{
                     padding: '10px 14px',
                     borderRight: '1px solid #e6eaee',
                     borderBottom: '1px solid #e6eaee',
                     backgroundColor: '#f4f7f9',
                     fontWeight: 'bold',
                     color: '#8C9EA7',
                     fontSize: '12px',
                     textTransform: 'uppercase'
                   }}
                  >
                    {getMultiFieldHeaderContent(f, rows[0], column)}
                  </div>
              ))}
              {rows.map((multi_data, rowIndex) => (
                <React.Fragment key={getMultiFieldRowKey(f.id, multi_data, rowIndex)}>
                  {displayColumns.map(column => (
                    <div
                      key={`lcell-${getMultiFieldRowKey(f.id, multi_data, rowIndex)}-${column.id}`}
                      style={{
                        padding: '10px 14px',
                        borderRight: '1px solid #e6eaee',
                        borderBottom: '1px solid #e6eaee'
                      }}
                    >
                      {renderCellContent(column, multi_data, rowIndex)}
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const checkConditionalValue = (field) => {
    let isValue = false;
    const conditionalKey = field?.conditional_field?.key;
    const conditionalStep = field?.conditional_step;
    let haveAnswers = data || {};
    if (conditionalStep === 'form_entry') {
      haveAnswers = form_entry_data;
    }
    if (formApprovalProcess?.length && conditionalStep !== 'form_entry') {
      let stepsAnswers = formApprovalProcess.find(useStep => useStep.step_id === conditionalStep) || {};
      haveAnswers = { ...stepsAnswers?.data };
    }
    let checkConditionalVal = haveAnswers[conditionalKey];
    if (typeof checkConditionalVal === 'string') {
      isValue = field?.conditional_values.filter(
        value => checkConditionalVal === value.value
      ).length > 0;
    }
    if (typeof checkConditionalVal === 'object') {
      if (Array.isArray(checkConditionalVal)) {
          isValue = field?.conditional_values.some(
              value => checkConditionalVal.indexOf(value.value) !== -1
          );
      } else if (checkConditionalVal?.level_1) {
        isValue = Object.values(checkConditionalVal)
              ?.filter(value => value !== '')
              ?.join()
              === Object.values(field.conditional_values[0])
              ?.filter(value => value !== '')
              ?.join();
      }
  } else if (typeof checkConditionalVal === 'boolean') {
      isValue = field?.conditional_values.filter(
        value => (
          checkConditionalVal.toString() === value?.value
        )
      ).length > 0;
    }

    if (adp) isValue = true;
    return isValue;
  };

  // CHECK THE FIELD VALUE IS PRESENT OR NOT
  const checkValueInFormEntry = (conditionalField) => {
    let isVal = false;
    const conditionalKey = conditionalField?.conditional_field?.key;
    const conditionalStep = conditionalField?.conditional_step;
    if (conditionalStep !== 'form_entry') {
      isVal = data[conditionalKey] !== '';
    }
    if (form_entry_data && conditionalStep === 'form_entry') {
      isVal = form_entry_data[conditionalStep] !== '';
    }
    if (formApprovalProcess?.length && conditionalStep !== 'form_entry') {
      let stepsAnswers = formApprovalProcess.find(useStep => useStep.step_id === conditionalStep)?.data || {};
      isVal = stepsAnswers[conditionalStep] !== '';
    }
    if (adp) isVal = true;
    return isVal;
  };

  const handleWildlifeSpeciesField = wildlifeField => (
    <div
    key={wildlifeField.id}
    className={styles.infoFull}
    style={{ padding: '0' }}
  >
    <span style={{ borderBottom: '1px solid #E6EAEE', padding: '10px 16px', margin: '0' }}>{wildlifeField.title}</span>
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <div className={`${styles.wildLifeFieldsCells} ${styles.borderRight}`}>
        <FormattedMessage
          id="wildlife.form.type"
          defaultMessage="Wildlife Type"
        />
        <p style={{ margin: '0' }}>{data[wildlifeField.id]?.type || ''}</p>
      </div>
      <div className={styles.wildLifeFieldsCells}>
        <FormattedMessage
          id="wildlife.form.species"
          defaultMessage="Wildlife Species"
        />
        <p style={{ margin: '0' }}>{data[wildlifeField.id]?.species || ''}</p>
      </div>
    </div>
    </div>
  );

  const handleFormDataSourceValue = fieldInput => (
    <>
      {fieldInput?.dataSourceID === 'ds6'
        && ((!Array.isArray(data[fieldInput.id]) && data[fieldInput.id]?.detailId)
        || (Array.isArray(data[fieldInput.id]) && data[fieldInput?.id]?.every(d => d?.detailId))) && (
          <>
            <div
              key={fieldInput.id}
              className={fieldInput.full_width ? styles.infoFull : `${styles.infoHalf} ${styles.halfWidth}`}
            >
              <span>{fieldInput.title}</span>
              <p>
                {data[fieldInput.id] && (
                  renderInfoForType(fieldInput, Array.isArray(data[fieldInput.id])
                  ? data[fieldInput?.id] : data[fieldInput.id]?.value, version, version?.schema, false, profile)
                )}
                <FormDataSourceDetails
                  link
                  FormDetailID={Array.isArray(data[fieldInput.id]) ? data[fieldInput?.id] : data[fieldInput.id]?.detailId}
                  showInLineTable={fieldInput?.settings?.show_values_in_inline_table_view}
                />
              </p>
            </div>
          </>
        )}
    </>
  );


  const handleMultiFieldString = (e, id, fieldId, isMultipleEmail) => {
     const value = typeof e === 'string' ? e : '';
  const formattedValue = isMultipleEmail
    ? value
        .split(',')
        .filter(v => typeof v === 'string')
        .map(v => v.trim())
        .join(', ')
    : value;

  if (isMultipleEmail) {
    return (
      <div style={{ width: '100%', wordBreak: 'break-word' }}>
        {formattedValue}
      </div>
    );
  }
  const popoverId = `popover${id}${fieldId}`;
  return (
    <>
      <div
        id={popoverId}
        style={{ cursor: 'pointer' }}
        onMouseEnter={() => setPopoverData({ isOpenPopover: true, selectedId: popoverId })
        }
        onMouseLeave={() => setPopoverData({ isOpenPopover: false, selectedId: '' })
        }
      >
        {formattedValue.substring(0, 40)} ...
      </div>
      <Popover trigger="hover legacy"
        placement="right"
        isOpen={
          popoverData?.selectedId === popoverId
          && popoverData?.isOpenPopover
        }
        target={popoverId}
        className={styles.desPopover}
      >
        <PopoverBody style={{ padding: '10px 10px 10px 15px' }}>
          <span className={styles.popoverDes}>
            {e}
          </span>
        </PopoverBody>
      </Popover>
    </>
  );
  };

  const getAddress = (value) => {
    const addressInfo = [value?.street, value?.fulladdress, value?.zip, value?.country?.value];
    let updatedAddress = [];
    if (addressInfo) {
      addressInfo.map((item) => {
        if (item !== '' && item !== null && item !== undefined) {
          updatedAddress = [
            ...updatedAddress,
            item
          ];
        }
        return null;
      });
    }
    return updatedAddress.join(', ');
  };

const getToggleText = (f, toggleData) => {
  if (toggleData[f.id] === true) {
    return f.settings.enableToggleText.length > 0
      ? f.settings.enableToggleText
      : 'yes';
  }
    return f.settings.disableToggleText.length > 0
      ? f.settings.disableToggleText
      : 'no';
};
  const airportCoordinates = profile?.airport?.location?.coordinates || profile?.portal_airport?.location?.coordinates;
  return (
    <div className={`${styles.stepInfo} ${styles.noMarginTop} ${noBorder && styles.noBorderSty}`}>
      {fields?.map(f => (
        <>
          {!f.conditional && (orderingField === f?.id || !orderingField) && !f.hide_for_all_users && (
            <>
              {f?.type === 'header' && f?.type !== 'map_location' && (
              <div key={f?.id} className={`${styles.infoFull} ${styles.headerSty}`}>
                <h3>{f?.title}</h3>
              </div>
              )}
              {f?.type === 'map_location' && f?.shape !== 'All'
                && !f?.settings?.allow_multiple_location && (
                <>
                  {f?.address_location && (
                    <div key={f?.id} className={styles.infoFull}>
                      <span>
                        <FormattedMessage
                          id="customfield.address"
                          defaultMessage="Address"
                        />
                      </span>
                      <p>{data[f.id]?.address}</p>
                    </div>
                  )}
                  {data[f?.id] && (
                    <div key={f?.id} className={styles.infoMap}>
                      <span>{f?.title}</span>
                      <div className={styles.map}>
                        <FormsMap
                          shape={f?.shape}
                          location={data[f?.id]}
                          defaultlocation={location || profile?.portal_airport?.location?.coordinates || []}
                          mapStyle={mapStyle || profile?.portal_airport?.airport_settings}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
              {f?.type === 'map_location' && (f?.shape === 'All' || f?.settings?.allow_multiple_location) && (
                <div key={f.id} className={styles.infoMap}>
                  <span>{f?.title}</span>
                  <div className={styles.map}>
                    <MultiLocation
                      answer={data[f.id]}
                      handleFieldErrorChanged={() => { }}
                      handleValueChange={() => { }}
                      fromForms
                      onlyLocationView
                      disabled
                      shape={f?.shape !== 'All' && f?.shape}
                      allowMultiple={f?.settings?.allow_multiple_location}
                      airportInfo={{
                        location: profile?.portal_airport?.location?.coordinates,
                        map_style: profile?.portal_airport?.airport_settings?.map_style,
                        mapbox_style: profile?.portal_airport?.airport_settings?.mapbox_style,
                        custom_map_url: profile?.portal_airport?.airport_settings?.custom_map_url
                      }}
                    />
                  </div>
                </div>
              )
              }
              {f.type === 'annotation' && f?.annotate_attachment?.length > 0 && f?.annotate_attachment[0]?.url && data[f.id] && (
              <div
              key={f.id}
              className={styles.infoFull}
              >
               <span>{f.title}</span>
               <Annotations imgSrc={(data[f.id]?.length > 0 && data[f.id][0]?.url)? data[f.id][0]?.url : f?.annotate_attachment[0]?.url}
                    existingImgSrc={f?.annotate_attachment[0]?.url} existingPaths={data[f?.id] || []} drawMode={false} />
              </div>)}
              {f?.type === 'attachments' && data && (
              <div style={{ display: 'grid', gridTemplateColumns: '100%', width: '50%' }}>
              <AttachmentDetail attachments={data[f?.id]} title={f?.title} adp enabled_zoomed={enabled_zoomed} />
              </div>
              )}
              {((f.type === 'wildlife_species')
              || (f.type === 'dataSource' && f.dataSourceID === 'ds10'))
              && Object.keys(data[f.id] || {})?.length > 0
              && (
              <>
              {handleWildlifeSpeciesField(f)}
              </>)}


              {f?.type === 'dataSource' && (
                <>
                {handleFormDataSourceValue(f)}
                </>
              )}


              {f.type?.includes('MultiLevel') && !(f?.dataSourceID === 'ds2'
                && ((FormDetails || formDetails) || printView || customPrintView)) && (
                <>
                  {f?.levels && Object.keys(f?.levels).map(level => (
                    <div key={`${f?.id}-${level}`} className={styles.infoHalf}>
                      <span>{f?.levels[level]?.title}</span>
                      <p>{data[f?.id]?.[level]}</p>
                    </div>
                  ))}
                </>
              )}
              {f.type === 'multi_field'
              && f?.multiple_fields
              && (() => {
                const multiFields = Object.keys(f?.multiple_fields || {});
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', borderTop: '1px solid #E6EAEE' }}>
                    {multiFields.map((multi_field) => {
                      if (f?.multiple_fields?.[multi_field]?.codeValues) {
                        const selectedKey = data[f.id]?.[0]?.[multi_field];
                        const codedValue = f?.multiple_fields?.[multi_field]?.values?.find(
                          item => item.key === selectedKey
                        )?.coded_value;
                        return (
                          <div
                            key={`${f.id}_${multi_field}`}
                            className={f.full_width ? styles.infoFull : `${styles.infoHalf} ${styles.halfWidth}`}
                            style={{ minWidth: '0' }}
                          >
                            <span>{f?.multiple_fields?.[multi_field]?.textForCodedValues}</span>
                            <p>{codedValue}</p>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                );
              })()}
              {f.type === 'multi_level'
                && data?.[f?.id] && (() => {
                  const seenCategories = {};
                  return Object.values(data?.[f.id]).filter(val => val).map((category) => {
                    seenCategories[category] = (seenCategories[category] || 0) + 1;
                    const occurrence = seenCategories[category];
                    return (
                      <div key={`${f?.id}-${category}-${occurrence}`} className={styles.infoHalf}>
                        <span>{occurrence === 1 ? `${f?.title}` : `${f?.title} ${occurrence - 1}`}</span>
                        <p>{category}</p>
                      </div>
                    );
                  });
                })()}
               {f.type=== 'toggle' && (
               <div key={f.id} className={`${styles.infoHalf} ${styles.halfWidth}`}>
                 <span>{f.title}</span>
                 <p> {getToggleText(f, data)}</p>
               </div>
             )}
              {(f.type?.includes('MultiLevel') || f.type === 'multi-level-location') && f?.dataSourceID === 'ds2'
                && ((FormDetails || formDetails) || printView || customPrintView) && (
              <>
                {f?.levels && Object.keys(f.levels).map((level, index) => (
                  <React.Fragment key={`${f.id}_${level}`}>
                    <div className={styles.infoHalf}>
                      <span>{f.levels[level]?.title || level?.replace('_', ' ')}</span>
                      <p>{data[f.id]?.[level]}</p>
                      {(FormDetails || formDetails)
                      && data[f.id]?.location_marked
                      && data[f.id]?.location_attachment
                      && data[f.id]?.location_attachment?.length > 0 && index === 0 && (
                        <div>
                          <Clickable
                            onClick={() => setTerminalModal({ terminalModal: !terminalModal?.terminalModal, fieldId: f.id })}
                            className={styles.terminalLink}
                          >
                            <FormattedMessageWithClass
                              id="maintenance.custom_location.click_notation_detail"
                              defaultMessage="Click here to view location on map"
                              className={styles.terminalLink}
                            />
                          </Clickable>
                        </div>
                      )}
                    </div>
                    {((FormDetails || formDetails) && ((data[f.id]?.location_marked
                      && data[f.id]?.location_attachment
                      && data[f.id]?.location_attachment?.length > 0)
                      || data[f.id]?.map_location))
                      && terminalModal?.fieldId === f.id && index === 0 && (
                        <div className={styles.locationTitle}>
                          <PictureDraggable
                            attachment={data[f.id]?.location_attachment?.[0]}
                            display={terminalModal?.terminalModal}
                            handleDisplay={() => setTerminalModal({ terminalModal: !terminalModal?.terminalModal, fieldId: f.id })}
                            detail
                            position={data[f.id]?.location_marked}
                            location={f.levels[level]?.title}
                            fromSMS
                          />
                        </div>
                    )}
                    {(data[f.id]?.map_location?.length > 0)
                    && index === 0 && airportCoordinates && (
                      <div className={styles.infoMap}>
                        <div className={styles.map}>
                          <MapLocationDetail
                            shape="Point"
                            location={{ coordinates: [...(data[f.id]?.map_location || [])].reverse(), type: 'Point' }}
                          />
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </>
            )}
              {f?.type === 'multi_field' && renderMultiFieldContent(f)}
              {!f.type?.includes('MultiLevel') && f?.type !== 'header' && f.type !== 'country' && f.type !== 'list_states'
                    && f.type !== 'page_break' && f?.type !== 'multi_field' && f.type !== 'annotation' && f.type !== 'dataSource'
              && f?.type !== 'map_location' && f?.type !== 'address' && f.type !== 'multi_level' && f.type !== 'inspection_checklist'
              && f?.type !== 'attachments' && f?.type !== 'signature' && (f.type !== 'wildlife_species' && f.dataSourceID !== 'ds10')
              && f?.type !== 'multiselect' && f.type !== 'toggle' && f.dataSourceID !== 'ds13' && f.type !== 'risk_assess'
              && f.type !== 'suffix' && (
                <>
                <div
                  key={f?.id}
                  className={(() => {
                 if (f?.id === 'd2') return styles.infoFull;
                  if ((f?.widget && f?.widget?.type === 'textfield') || f?.type === 'header') {
                return styles.infoFull;
                }
                return `${styles.infoHalf} ${styles.halfWidth}`;
                })()}

                >
                  <span>{f?.title}</span>
                  <p>
                      {data?.[f?.id]?.toString() && (
                        f?.id !== 'issusing_authority' && f?.type !== 'duration'
                          ? renderInfoForType(
                            f,
                            typeof data?.[f?.id] === 'string'
                            && data[f?.id].includes(',')
                            && data[f?.id]
                              .split(',')
                              .filter(v => typeof v === 'string')
                              .map(v => v.trim())
                              .join(', ') || data?.[f?.id],
                            version,
                            version?.schema,
                            badging,
                            profile,
                            fromBadgeSignatory
                          )
                          : (
                            typeof data?.[f?.id] === 'string'
                            && data[f?.id].includes(',')
                            && data[f?.id]
                              .split(',')
                              .filter(v => typeof v === 'string')
                              .map(v => v.trim())
                              .join(', ') || data?.[f?.id]
                          )
                      )}
                  </p>
                </div>
                {f.type === 'select' && f.settings?.codeValues && (
                  <div key={f.id} className={f.full_width ? styles.infoFull : `${styles.infoHalf} ${styles.halfWidth}`}>
                    <span>{f?.settings?.textForCodedValues}</span>
                      <p>
                          {f.values[data[f.id]].coded_value}
                      </p>
                  </div>)}
                </>
              )}
              {badging && f.type === 'suffix' && (
                <div key={f.id} className={f.full_width ? styles.infoFull : `${styles.infoHalf} ${styles.halfWidth}`}>
                  <span>{f?.title}</span>
                  <p>
                  {renderInfoForType(f, data[f.id], badgeData, version?.schema, true, profile)}
                  </p>
                </div>
              )}
              {(f.type === 'country' || f.type === 'list_states')
              && (
              <div key={f?.id} className={`${styles.infoHalf} ${styles.halfWidth}`}>
                <span>{f?.title}</span>
                <p>
                  {data[f?.id]
                  && data[f?.id]?.label}
                </p>
              </div>
              )}
              {f?.type === 'signature' && (
                <div
                  key={f?.id}
                  className={styles.infoSignature}
                >
                  <span>{f?.title}</span>
                  {data[f?.id] && data[f?.id][0]
                  && <img className={styles.signWidth} maxHeight="150px" maxWidth="500px" src={data[f.id][0].url} alt="" />}
                </div>
              )}
              {f?.type === 'address' && data?.[f?.id] && typeof data?.[f?.id] === 'object'
                ? (
                  <div
                    key={f?.id}
                    className={f.full_width ? styles.infoFull : `${styles.infoHalf} ${styles.halfWidth}`}
                  >
                    <span>{f.title}</span>
                    <p>{getAddress(data?.[f?.id])}</p>
                  </div>
              )
                : f?.type === 'address' && (
                <div
                  key={f?.id}
                  className={f.full_width ? styles.infoFull : `${styles.infoHalf} ${styles.halfWidth}`}
                >
                  <span>{f.title}</span>
                  <p>{data[f?.id]}</p>
                </div>
              )}
              {f.type === 'inspection_checklist' && (
                <>
                <div
                  key={f.id}
                  className={styles.infoFull}
                >
                  <span>{f.title}</span>
                </div>
                <CheckListDisplay
                  useFieldData={f}
                  handleItemStatusChange={() => ({})}
                  pass={undefined}
                  check_list_answers={data?.[f?.id]}
                  displayChecklist
                />
                </>
              )}
              {fromBadgeSignatory && f.type === 'multiselect' && (
                <div
                key={f?.id}
                className={f.full_width ? styles.infoFull : `${styles.infoHalf} ${styles.halfWidth}`}
                >
                <span>{f?.title}</span>
                <p>{renderInfoForType(f, data[f?.id], version, version?.schema, undefined, profile, fromBadgeSignatory)}</p>
                </div>
              )}
            </>
          )}
          {f?.conditional && (orderingField === f?.id || !orderingField) && !f.hide_for_all_users
          && checkValueInFormEntry(f)
          && checkConditionalValue(f) && (
            <>
              {f?.type === 'header' && f?.type !== 'map_location' && (
                <div key={f?.id} className={styles.infoFull}>
                  <h3>{f?.title}</h3>
                </div>
              )}
              {f?.type === 'map_location' && f?.shape !== 'All'
                && !f?.settings?.allow_multiple_location && (
                <>
                    {f?.address_location && (
                      <div key={f?.id} className={styles.infoFull}>
                        <span>
                          <FormattedMessage
                            id="customfield.address"
                            defaultMessage="Address"
                          />
                        </span>
                        <p>{data[f?.id]?.address}</p>
                      </div>
                    )}
                    {data[f?.id] && (
                      <div key={f?.id} className={styles.infoMap}>
                        <span>{f?.title}</span>
                        <div className={styles.map}>
                          <FormsMap
                            shape={f?.shape}
                            location={data[f?.id]}
                            defaultlocation={location || profile?.portal_airport?.location?.coordinates || []}
                            mapStyle={mapStyle || profile?.portal_airport?.airport_settings}
                          />
                        </div>
                      </div>
                    )}
                </>
              )}
              {f?.type === 'map_location' && (f?.shape === 'All' || f?.settings?.allow_multiple_location) && (
                <div key={f.id} className={styles.infoMap}>
                  <span>{f?.title}</span>
                  <div className={styles.map}>
                    <MultiLocation
                      answer={data[f.id]}
                      handleFieldErrorChanged={() => { }}
                      handleValueChange={() => { }}
                      fromForms
                      onlyLocationView
                      disabled
                      shape={f?.shape !== 'All' && f?.shape}
                      allowMultiple={f?.settings?.allow_multiple_location}
                      airportInfo={{
                        location: profile?.portal_airport?.location?.coordinates,
                        map_style: profile?.portal_airport?.airport_settings?.map_style,
                        mapbox_style: profile?.portal_airport?.airport_settings?.mapbox_style,
                        custom_map_url: profile?.portal_airport?.airport_settings?.custom_map_url
                      }}
                    />
                  </div>
                </div>
              )
              }
              {f?.type === 'attachments' && data[f?.id] && (
                <AttachmentDetail attachments={data[f?.id]} title={f?.title} enabled_zoomed={enabled_zoomed} />
              )}
              {((f.type === 'wildlife_species')
              || (f.type === 'dataSource' && f.dataSourceID === 'ds10'))
              && Object.keys(data[f.id] || {})?.length > 0
              && (
              <>
                {handleWildlifeSpeciesField(f)}
              </>)}
              {(f.type?.includes('MultiLevel') || f.type === 'multi-level-location') && f?.dataSourceID === 'ds2'
                && ((FormDetails || formDetails) || printView || customPrintView) && (
                  <>
                    {f?.levels && Object.keys(f.levels).map((level, index) => (
                      <React.Fragment key={`${f.id}_${level}`}>
                        <div className={styles.infoHalf}>
                          <span>{f.levels[level]?.title || level?.replace('_', ' ')}</span>
                          <p>{data[f.id]?.[level]}</p>
                          {(FormDetails || formDetails)
                          && data[f.id]?.location_marked
                          && data[f.id]?.location_attachment
                          && data[f.id]?.location_attachment?.length > 0 && index === 0 && (
                            <div>
                              <Clickable
                                onClick={() => setTerminalModal({ terminalModal: !terminalModal?.terminalModal, fieldId: f.id })}
                                className={styles.terminalLink}
                              >
                                <FormattedMessageWithClass
                                  id="maintenance.custom_location.click_notation_detail"
                                  defaultMessage="Click here to view location on map"
                                  className={styles.terminalLink}
                                />
                              </Clickable>
                            </div>
                          )}
                        </div>
                        {((FormDetails || formDetails) && ((data[f.id]?.location_marked
                          && data[f.id]?.location_attachment
                          && data[f.id]?.location_attachment?.length > 0)
                          || data[f.id]?.map_location))
                          && terminalModal?.fieldId === f.id && index === 0 && (
                            <div className={styles.locationTitle}>
                              <PictureDraggable
                                attachment={data[f.id]?.location_attachment?.[0]}
                                display={terminalModal?.terminalModal}
                                handleDisplay={() => setTerminalModal({ terminalModal: !terminalModal?.terminalModal, fieldId: f.id })}
                                detail
                                position={data[f.id]?.location_marked}
                                location={f.levels[level]?.title}
                                fromSMS
                              />
                            </div>
                        )}
                        {(data[f.id]?.map_location?.length > 0)
                        && index === 0 && airportCoordinates && (
                          <div className={styles.infoMap}>
                            <div className={styles.map}>
                              <MapLocationDetail
                                shape="Point"
                                location={{ coordinates: [...data[f.id]?.map_location].reverse(), type: 'Point' }}
                              />
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </>
              )}
              {f.type.includes('MultiLevel') && !(f?.dataSourceID === 'ds2'
                && ((FormDetails || formDetails) || printView || customPrintView)) && (
                <>
                  {f?.levels && Object.keys(f?.levels).map(level => (
                    <div key={`${f?.id}-${level}`} className={styles.infoHalf}>
                      <span>{f?.levels[level]?.title}</span>
                      <p>{data[f.id]?.[level]}</p>
                    </div>
                  ))}
                </>
              )}
              {f.type === 'multi_level'
                && data?.[f?.id] && Object.values(data?.[f.id]).filter(val => val).map((category, index) => (
                  <div key={`${f?.id}-${category}`} className={styles.infoHalf}>
                    <span>{index === 0 ? `${f?.title}` : `${f?.title} ${index}`}</span>
                    <p>{category}</p>
                  </div>
              ))}

              {f?.type === 'dataSource' && (
                <>
                  {handleFormDataSourceValue(f)}
                </>
              )}

              {f.type=== 'toggle' && (
               <div key={f.id} className={`${styles.infoHalf} ${styles.halfWidth}`}>
                 <span>{f.title}</span>
                 <p> {getToggleText(f, data)}</p>
               </div>
             )}
             {f.type === 'multi_field'
              && f?.multiple_fields
              && Object.keys(f?.multiple_fields).map((multi_field) => {
                if (f?.multiple_fields?.[multi_field]?.codeValues) {
                  const selectedKey = data[f.id]?.[0]?.[multi_field];
                  const codedValue = f?.multiple_fields?.[multi_field]?.values?.find(
                    item => item.key === selectedKey
                  )?.coded_value;
                  return (
                    <div
                      key={`${f.id}_${multi_field}`}
                      className={
                        f.full_width ? styles.infoFull : `${styles.infoHalf} ${styles.halfWidth}`
                      }
                    >
                      <span>{f?.multiple_fields?.[multi_field]?.textForCodedValues}</span>
                      <p>{codedValue}</p>
                    </div>
                  );
                }
                return null;
              })}
              {f.type === 'multi_field' && renderMultiFieldContent(f)}
              {f.type === 'annotation' && f?.annotate_attachment?.length > 0 && f?.annotate_attachment[0]?.url && data[f.id] && (
                <div
                  key={f.id}
                  className={styles.infoFull}
                >
                  <span>{f.title}</span>
                  <Annotations imgSrc={(data[f.id]?.length > 0 && data[f.id][0]?.url)? data[f.id][0]?.url : f?.annotate_attachment[0]?.url}
                    existingImgSrc={f?.annotate_attachment[0]?.url} existingPaths={data[f?.id] || []} drawMode={false} />
                </div>)}
              {!f.type.includes('MultiLevel') && f?.type !== 'header' && f.type !== 'country' && f.type !== 'list_states'
                    && f.type !== 'page_break' && f?.type !== 'multi_field' && f.type !== 'annotation' && f.type !== 'dataSource'
                && f?.type !== 'map_location' && f.type !== 'multi_level' && (f.type !== 'wildlife_species' && f.dataSourceID !== 'ds10')
                && f?.type !== 'attachments' && f?.type !== 'signature' && f.type !== 'toggle'
                && f.type !== 'suffix' && (
                <>
                  <div
                    key={f?.id}
                    className={
                      (f?.widget && f?.widget?.type === 'textfield')
                      || f?.type === 'header'
                        ? styles.infoFull
                        : `${styles.infoHalf} ${styles.halfWidth}`
                    }
                  >
                    <span>{f?.title}</span>
                    <p>
                    {data?.[f?.id]?.toString() && (
                      f?.id !== 'issusing_authority'
                        ? renderInfoForType(f, data[f?.id], version, version?.schema, badging, profile, fromBadgeSignatory)
                        : data?.[f?.id]
                    )}
                    </p>
                  </div>
                  {f.type === 'select' && f.settings?.codeValues && (
                    <div key={f.id} className={f.full_width ? styles.infoFull : `${styles.infoHalf} ${styles.halfWidth}`}>
                      <span>{f?.settings?.textForCodedValues}</span>
                        <p>
                            {f.values[data[f.id]].coded_value}
                        </p>
                    </div>)}
                </>
              )}
              {badging && f.type === 'suffix' && (
                <div key={f.id} className={f.full_width ? styles.infoFull : `${styles.infoHalf} ${styles.halfWidth}`}>
                  <span>{f?.title}</span>
                  <p>
                  {renderInfoForType(f, data[f.id], badgeData, version?.schema, true, profile)}
                  </p>
                </div>
              )}
              {(f.type === 'country' || f.type === 'list_states')
              && (
              <div key={f?.id} className={`${styles.infoHalf} ${styles.halfWidth}`}>
                <span>{f?.title}</span>
                <p>
                  {data[f?.id]
                  && data[f?.id]?.label}
                </p>
              </div>
              )}
              {f?.type === 'signature' && (
              <div
                key={f?.id}
                className={styles.infoSignature}
              >
                <span>{f?.title}</span>
                {data[f?.id] && data[f?.id][0]
                && <img className={styles.signWidth} maxHeight="150px" maxWidth="500px" src={data[f?.id][0].url} alt="" />}
              </div>
              )}
            </>
          )}
        </>
      ))}
    </div>
  );
};

CustomFieldDetail.propTypes = {
  data: PropTypes.shape({}),
  fields: PropTypes.arrayOf(PropTypes.shape({})),
  mcgHeader: PropTypes.bool,
  selectedLang: PropTypes.string,
  linkedFormAnswerDetails: PropTypes.arrayOf(PropTypes.shape({})),
  user: PropTypes.shape({}),
  intl: PropTypes.shape({})
};

CustomFieldDetail.defaultProps = {
  data: {},
  fields: [],
  mcgHeader: false,
  selectedLang: 'en',
  linkedFormAnswerDetails: EMPTY_ARRAY,
  user: {},
  intl: {}
};

const mapStateToProps = state => ({
  allDataSourcesFromStore: state.inspection?.allDataSources || EMPTY_ARRAY,
  linkedFormAnswerDetails: state.forms?.linkedFormAnswerDetails || EMPTY_ARRAY,
  selectedLang: state.auth?.selectedLang || localStorage.getItem('lang') || 'en',
  user: state.auth?.profile
});

export default connect(mapStateToProps, {})(injectIntl(CustomFieldDetail));
